/* global google */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import glamorous from 'glamorous';
import debounce from 'lodash/debounce';
import { borders, type, dimensions, truncate } from '../../style';
import AutoSizeInput from '../common/AutoSizeInput';

const RootDiv = glamorous.div({
  position: 'relative',
  paddingBottom: '0px',
});

const formatCustomInput = (style) => {
  if (style === "mt-purple") {
    return {
      background: "transparent",
      color: "#CAB4FF",
      height: "inherit",
      fontSize: 24,
      fontFamily:"'PT Serif', serif",
      padding: 0,
      borderTop: 0,
      borderRight: 0,
      borderBottom: "1px dashed #979797",
      borderLeft: 0,
      lineHeight: "32px",
      ":focus": {
        borderTop: 0,
        borderRight: 0,
        borderBottom: "1px dashed #979797",
        borderLeft: 0,
        outline: "none"
      },
      "::placeholder": {
        fontStyle: "italic",
        color: "white",
        opacity: 0.5
      },
      [dimensions.SCREEN_MAX_XXS]: {
        fontSize: 22
      }
    }
  }
}

const ellipsify = (addEllipsis) => {
  return addEllipsis ? truncate : null
}

const StyledInput = glamorous.input({
  width: '100%',
  boxSizing: 'border-box',
  padding: '0 15px',
  height: '38px',
  outline: 'none',
  transition: 'border-color 0.2s ease-in-out',
  ...type.MD,

  ':focus': {
    border: borders.BRAND
  }
},
({ hasError, customInputStyle, addEllipsis }) => ({
  border: hasError ? borders.ERROR : borders.DARKER,
  ...formatCustomInput(customInputStyle),
  borderRadius: customInputStyle === "mt-purple" ? 0 : 3,
  ...ellipsify(addEllipsis)

}));

const AutocompleteContainer = glamorous.div({
  position: 'absolute',
  top: '100%',
  backgroundColor: 'white',
  border: borders.DARKER,
  borderRadius: '2px',
  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
  width: '100%',
  zIndex: '1000',
  ':after': {
    content: '""',
    padding: '1px 1px 1px 0',
    height: '16px',
    textAlign: 'right',
    display: 'block',
    backgroundImage: 'url(https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-non-white.png)',
    backgroundPosition: 'right',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '120px 14px',
  }
});

const AutocompleteItem = glamorous.div({
  backgroundColor: '#ffffff',
  fontSize: '13px',
  padding: '0 4px',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  lineHeight: '30px',
  textAlign: 'left',
  borderTop: '1px solid #e6e6e6',
  color: '#555555',
  cursor: 'default',
  ':hover': {
    backgroundColor: '#f9f9f9'
  }
}, ({isActive}) => {
  if(isActive) {
    return {
      backgroundColor: '#f5f5f5'
    }
  }
});

const PacIcon = glamorous.span({
  width: '15px',
  height: '20px',
  marginRight: '7px',
  marginTop: '6px',
  display: 'inline-block',
  verticalAlign: 'top',
  backgroundImage: 'url(https://maps.gstatic.com/mapfiles/api-3/images/autocomplete-icons.png)',
  backgroundSize: '34px'
}, ({ isMarker }) => ({
  backgroundPosition: isMarker ? '-1px -161px' : ''
  })
);

class Autocomplete extends Component {
  constructor(props) {
    super(props);

    this.state = { autocompleteItems: [] };

    this.autocompleteCallback = this.autocompleteCallback.bind(this);
    this.handleInputKeyDown = this.handleInputKeyDown.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.debouncedFetchPredictions = debounce(this.fetchPredictions, this.props.debounce);
  };

  componentDidMount() {
    if(!window.google) {
      throw new Error('Google Maps Javascript API library must be loaded.');
    };

    if(!window.google.maps.places) {
      throw new Error('Google Maps Places library must be loaded.')
    };

    this.autocompleteService = new google.maps.places.AutocompleteService();
    this.autoCompleteOK = google.maps.places.PlacesServiceStatus.OK;
  };

  autocompleteCallback(predictions, status) {
    if(status !== this.autoCompleteOK) {
      this.props.onError(status);
      if(this.props.clearItemsOnError) {
        this.clearAutocomplete();
      }
      return;
    };

    const formattedSuggestion = (structured_formatting) => ({
      mainText: structured_formatting.main_text,
      secondaryText: structured_formatting.secondary_text
    });

    const { highlightFirstSuggestion } = this.props;

    this.setState({
      autocompleteItems: predictions.map((p, idx) => ({
        suggestion: p.description,
        placeId: p.place_id,
        active: (highlightFirstSuggestion && idx === 0 ? true : false),
        index: idx,
        formattedSuggestion: formattedSuggestion(p.structured_formatting)
      }))
    });
  };

  fetchPredictions() {
    const { value } = this.props.inputProps;
    if(value.length) {
      this.autocompleteService.getPlacePredictions({
        ...this.props.options,
        input: value
      }, this.autocompleteCallback)
    };
  };

  clearAutocomplete() {
    this.setState({ autocompleteItems: [] });
  };

  selectAddress(address, placeId) {
    this.clearAutocomplete();
    this.handleSelect(address, placeId);
  };

  handleSelect(address, placeId) {
    this.props.onSelect ? this.props.onSelect(address, placeId) : this.props.inputProps.onChange(address);
  };

  getActiveItem() {
    return this.state.autocompleteItems.find(item => item.active);
  };

  selectActiveItemAtIndex(index) {
    const activeName = this.state.autocompleteItems.find(item => item.index === index).suggestion;
    this.setActiveItemAtIndex(index);
    this.props.inputProps.onChange(activeName);
  };

  handleEnterKey() {
    const activeItem = this.getActiveItem();
    if(activeItem === undefined) {
      this.handleEnterKeyWithoutActiveItem();
    } else {
      this.selectAddress(activeItem.suggesstion, activeItem.placeId);
    };
  };

  handleEnterKeyWithoutActiveItem() {
    if(this.props.onEnterKeyDown) {
      this.props.onEnterKeyDown(this.props.inputProps.value);
      this.clearAutocomplete();
      this.debouncedFetchPredictions.cancel();
    } else {
      return; //noop
    };
  };

  handleDownKey() {
    if(this.state.autocompleteItems.length === 0) {
      return;
    };

    const activeItem = this.getActiveItem();
    if(activeItem === undefined) {
      this.selectActiveItemAtIndex(0);
    } else {
      const nextIndex = (activeItem.index + 1) % this.state.autocompleteItems.length
      this.selectActiveItemAtIndex(nextIndex);
    }
  }

  handleUpKey() {
    if(this.state.autocompleteItems.length === 0) {
      return;
    };

    const activeItem = this.getActiveItem();
    if(activeItem === undefined) {
      this.selectActiveItemAtIndex(this.state.autocompleteItems.length - 1);
    } else {
      let prevIndex;
      if(activeItem.index === 0) {
        prevIndex = this.state.autocompleteItems.length - 1;
      } else {
        prevIndex = (activeItem.index - 1) % this.state.autocompleteItems.length;
      };
      this.selectActiveItemAtIndex(prevIndex);
    }
  }

  handleInputKeyDown(event) {
    switch(event.key) {
      case 'Enter':
        event.preventDefault();
        this.handleEnterKey();
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.handleDownKey();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.handleUpKey();
        break;
      case 'Escape':
        event.preventDefault();
        this.clearAutocomplete();
        break;
      default:
        break;
    };

    if(this.props.inputProps.onKeyDown) {
      this.props.inputProps.onKeyDown(event);
    }
  };

  setActiveItemAtIndex(index) {
    this.setState({
      autocompleteItems: this.state.autocompleteItems.map((item, idx) => {
        if(idx === index) {
          return { ...item, active: true };
        } else {
          return { ...item, active: false };
        }
      })
    });
  };

  handleInputChange(event) {
    this.props.inputProps.onChange(event.target.value);
    if(!event.target.value) {
      this.clearAutocomplete();
      return;
    };

    this.debouncedFetchPredictions();
  };

  handleInputOnBlur(event) {
    this.clearAutocomplete();

    if(this.props.inputProps.onBlur) {
      this.props.inputProps.onBlur(event);
    };
  }

  getInputProps() {
    return {
      ...this.props.inputProps,
      autoComplete: 'off',
      onChange: (event) => {
        this.handleInputChange(event);
      },
      onKeyDown: (event) => {
        this.handleInputKeyDown(event);
      },
      onBlur: (event) => {
        this.handleInputOnBlur(event);
      },
      onFocus: (event) => {
        this.handleInputChange(event);
      }
    };
  };

  render() {
    const { autocompleteItems } = this.state;
    const inputProps = this.getInputProps();

    let defaultInput = (
      <StyledInput
        hasError={this.props.error}
        customInputStyle={this.props.custom}
        addEllipsis={this.props.addEllipsis}
        {...inputProps} />
    )

    let autoResizeInput = (
      <AutoSizeInput
        hasError={this.props.error}
        customInputStyle={this.props.custom}
        inputProps={inputProps}
        minWidth={238}
        maxLength={this.props.maxInputLength}
        addEllipsis={this.props.addEllipsis}
      />
    )

    return (
      <RootDiv custom={this.props.custom}>
        { this.props.autoResize ? autoResizeInput : defaultInput}
        { autocompleteItems.length > 0 && (
          <AutocompleteContainer>
            {autocompleteItems.map((p, idx) => (
              <AutocompleteItem
                key={p.placeId}
                onMouseOver={() => this.setActiveItemAtIndex(p.index)}
                onMouseDown={() => this.selectAddress(p.suggestion, p.placeId)}
                //Add Touch Events to work on mobile
                onTouchStart={(e) => {e.preventDefault(); this.setActiveItemAtIndex(p.index)}}
                onTouchEnd={(e) => {e.preventDefault(); this.selectAddress(p.suggestion, p.placeId)}}
                isActive={p.active}

              >
                {this.props.autocompleteItem({ suggestion: p.suggestion, formattedSuggestion: p.formattedSuggestion })}
              </AutocompleteItem>
            ))}
          </AutocompleteContainer>
        )}
      </RootDiv>
    )
  }
}

Autocomplete.propTypes = {
  inputProps: (props, propName) => {
    const inputProps = props[propName];

    if(!inputProps.hasOwnProperty('value')) {
      throw new Error(`'inputProps' must have 'value'.`);
    };

    if(!inputProps.hasOwnProperty('onChange')) {
      throw new Error(`'inputProps' must have 'onChange'`);
    };
  },
  onError: PropTypes.func,
  clearItemsOnError: PropTypes.bool,
  onSelect: PropTypes.func,
  autocompleteItem: PropTypes.func,
  options: PropTypes.shape({
    bounds: PropTypes.object,
    componentRestrictions: PropTypes.object,
    location: PropTypes.object,
    offset: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string
    ]),
    radius: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string
    ]),
    types: PropTypes.array
  }),
  debounce: PropTypes.number, // In milliseconds (ms)
  highlightFirstSuggestion: PropTypes.bool
};

Autocomplete.defaultProps = {
  clearItemsOnError: true,
  onError: (status) => console.error(
    '[Autocomplete]: error happened when fetching data from Google Maps API.\nPlease check the docs here (https://developers.google.com/maps/documentation/javascript/places#place_details_responses)\nStatus: ',
    status),
  autocompleteItem: ({ suggestion }) => (<div><PacIcon isMarker></PacIcon>{ suggestion }</div>),
  options: {
    types: ['(cities)']
  },
  debounce: 500,
  highlightFirstSuggestion: true,
};

export default Autocomplete;



// WEBPACK FOOTER //
// ./src/components/common/AutoComplete.jsx